import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * QuickTooltip - 一个快速响应、胶囊形状的自定义提示组件
 * 用于替代原生 title 属性，解决延迟高和样式不可控的问题。
 *
 * 使用 position:fixed + getBoundingClientRect 计算坐标，
 * 通过 ReactDOM.createPortal 渲染到 document.body，
 * 完全逃出任何 overflow:hidden 的祖先容器的裁切限制。
 */
const QuickTooltip = ({ content, children, position = 'top', delay = 300 }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);

  // 根据触发元素的 BoundingClientRect 计算 fixed 坐标
  const calcCoords = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const GAP = 8; // tooltip 与触发元素之间的间距（px）
    switch (position) {
      case 'bottom':
        setCoords({ top: rect.bottom + GAP, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' });
        break;
      case 'left':
        setCoords({ top: rect.top + rect.height / 2, left: rect.left - GAP, transform: 'translate(-100%, -50%)' });
        break;
      case 'right':
        setCoords({ top: rect.top + rect.height / 2, left: rect.right + GAP, transform: 'translateY(-50%)' });
        break;
      default: // top
        setCoords({ top: rect.top - GAP, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)' });
        break;
    }
  }, [position]);

  const handleMouseEnter = () => {
    calcCoords();
    timerRef.current = setTimeout(() => {
      calcCoords(); // 延迟后再算一次，防止快速滚动导致位置偏移
      setVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  };

  // 全局 mousedown 监听：点击任何地方时隐藏 tooltip
  useEffect(() => {
    if (!visible) return;
    const handleGlobalMouseDown = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setVisible(false);
    };
    window.addEventListener('mousedown', handleGlobalMouseDown, true); // capture phase
    return () => window.removeEventListener('mousedown', handleGlobalMouseDown, true);
  }, [visible]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!content) return children;

  const tooltip = visible
    ? ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          transform: coords.transform,
          backgroundColor: '#ffffff',
          color: '#1e293b',
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 99999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          border: 'none',
        }}>
          {content}
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseLeave} // 点击时立即隐藏
    >
      {children}
      {tooltip}
    </div>
  );
};

export default QuickTooltip;
